// Connecting to the '/meta-models/' endpoint to get metadata schema definitions.

// These are all loaded at application startup, and get connected to responses
// from scenario endpoints to form the Scenario domain model.

import { useContext } from "react";
import { z } from "zod";

import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { useParsedQuery } from "@nens/react-query-utils";

import { API_BASE_URL } from "../constants";
import { dateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

const metaModelPropertySchema = z.object({
  // These are in Dutch, english fields should be added at some point
  // but Dutch is default
  title: z.string(),
  description: z.string(),
  key: z.string(),

  type: z.union([
    z.literal("array"),
    z.literal("string"),
    z.literal("number"),
    z.literal("integer"),
    z.literal("boolean"),
  ]),
  enum: z.array(z.string()).nullable(),
  pattern: z.string().nullable(),
  format: z.string().nullable(),
  readOnly: z.boolean(),
  i18n: z
    .object({
      en: z
        .object({
          title: z.string(),
          description: z.string(),
        })
        .nullable(),
    })
    .nullable(),
});

const metaModelSchema = z.object({
  id: z.number(),
  json_schemas: z.array(
    z.object({
      title: z.string(),
      properties: z.array(metaModelPropertySchema),
    })
  ),
  updated_at: dateSchema,
});

export type MetaModel = z.infer<typeof metaModelSchema>;

export function useMetaModels() {
  const url = `${API_BASE_URL}/meta-models`;
  const { apiFetchFunction } = useContext(LdoAuthContext);

  return useParsedQuery<MetaModel[]>(
    ["ldo-api-v1", "meta-model"],
    () => apiFetchFunction(url),
    (json: unknown) => paginatedSchema(metaModelSchema).parse(json).items
  );
}

export function templateDownloadUrl(latestMetaModelId: number, scenarioType?: string) {
  if (scenarioType === undefined) {
    return `${API_BASE_URL}/scenarios/template/?version=${latestMetaModelId}`;
  }
  return `${API_BASE_URL}/scenarios/template/?version=${latestMetaModelId}&scenario_type=${scenarioType}`;
}
